/**
 * Which computed columns the *server* has to produce.
 *
 * A computed column is placed, not permitted: an expression the browser can
 * evaluate from one row stays a `valueGetter` and costs a round trip of nothing,
 * while one that reads bar history has to be materialised against the symbol's
 * bar file and joined into the snapshot. `needsHistory` is the whole test, and
 * it is derived from the AST rather than stored, so it cannot drift out of step
 * with the expression it describes.
 *
 * This lives here rather than in the app because the same split applies to every
 * host of this island (D18), and getting it wrong is silent: ask for too few and
 * the column is blank, too many and the server does work the browser already did.
 */

import type { ScreenerColumn } from "@qufolio/model";
import type { ScreenerComputedRequest } from "@qufolio/transport";

import { needsHistory } from "./expr.js";
import { historyConditions, type ScreenerFilter } from "./filter-eval.js";

/**
 * The subset of `columns` the server must compute, in the shape the socket
 * sends. Empty when nothing needs history — which keeps the server's channel
 * key unsplit for the overwhelmingly common case.
 */
export function serverComputedColumns(
  columns: readonly ScreenerColumn[] | undefined,
): ScreenerComputedRequest[] {
  return (columns ?? [])
    .filter((c) => c.computed && needsHistory(c.computed.expression))
    .map((c) => ({
      field: c.field,
      expression: c.computed!.expression,
      // `recompute` is the contract's own default and the right one for a
      // derived quantity: the group's value, not the mean of the members'.
      rollup: c.computed!.rollup ?? "recompute",
    }));
}

/**
 * The applied filter's history-needing ScanScript conditions, as hidden
 * computed fields — the server computes each like a column, and `filter-eval`
 * reads the answer. `none` rollup: a condition is a per-symbol answer, and a
 * group row has nothing to aggregate it into.
 */
export function serverConditionColumns(
  filter: ScreenerFilter | undefined,
): ScreenerComputedRequest[] {
  return historyConditions(filter).map(({ field, expression }) => ({
    field,
    expression,
    rollup: "none",
  }));
}
